import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

interface DropdownContextType {
  openDropdownId: string | null;
  openDropdown: (id: string) => void;
  closeDropdown: () => void;
  isOpen: (id: string) => boolean;
}

const DropdownContext = createContext<DropdownContextType | null>(null);

export function DropdownProvider({ children }: { children: ReactNode }) {
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const openDropdown = useCallback((id: string) => {
    setOpenDropdownId(id);
  }, []);

  const closeDropdown = useCallback(() => {
    setOpenDropdownId(null);
  }, []);

  const isOpen = useCallback((id: string) => {
    return openDropdownId === id;
  }, [openDropdownId]);

  // Close dropdown on Escape key
  useEffect(() => {
    if (!openDropdownId) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [openDropdownId, closeDropdown]);

  // Close dropdown on scroll (capture phase to catch all scrolls)
  // Add a small delay to prevent closing on initial render scroll events
  useEffect(() => {
    if (!openDropdownId) return;

    let isEnabled = false;
    const enableTimer = setTimeout(() => {
      isEnabled = true;
    }, 100); // Small delay to allow dropdown to render without being closed by layout shifts

    const handleScroll = () => {
      if (isEnabled) {
        closeDropdown();
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => {
      clearTimeout(enableTimer);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openDropdownId, closeDropdown]);

  return (
    <DropdownContext.Provider value={{ openDropdownId, openDropdown, closeDropdown, isOpen }}>
      {children}
    </DropdownContext.Provider>
  );
}

export function useDropdownContext() {
  const context = useContext(DropdownContext);
  if (!context) {
    throw new Error('useDropdownContext must be used within a DropdownProvider');
  }
  return context;
}
